import React, { useState, useMemo, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  AreaChart,
  BarChart,
  Bar,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from "@/components/header";
import {
  TrendingUp,
  DollarSign,
  Target,
  Plus,
  Trash2,
  Link,
} from "lucide-react";

// Popular startups data
const popularStartups = {
  stripe: {
    name: "Stripe",
    fundingRounds: [
      { round: "Seed", year: 2010, amount: 2000000, valuation: 20000000 },
      { round: "Series A", year: 2011, amount: 18000000, valuation: 100000000 },
      { round: "Series B", year: 2012, amount: 70000000, valuation: 500000000 },
      { round: "Series C", year: 2014, amount: 80000000, valuation: 1500000000 },
      { round: "Series D", year: 2016, amount: 150000000, valuation: 9000000000 },
      { round: "Series E", year: 2019, amount: 250000000, valuation: 35000000000 },
    ],
    ipoValuation: 95000000000,
    ipoYear: 2021,
  },
  airbnb: {
    name: "Airbnb",
    fundingRounds: [
      { round: "Seed", year: 2009, amount: 20000, valuation: 200000 },
      { round: "Series A", year: 2010, amount: 7200000, valuation: 72000000 },
      { round: "Series B", year: 2011, amount: 112000000, valuation: 1300000000 },
      { round: "Series C", year: 2014, amount: 475000000, valuation: 10000000000 },
      { round: "Series D", year: 2015, amount: 1500000000, valuation: 25500000000 },
    ],
    ipoValuation: 100000000000,
    ipoYear: 2020,
  },
  shopify: {
    name: "Shopify",
    fundingRounds: [
      { round: "Seed", year: 2006, amount: 250000, valuation: 2500000 },
      { round: "Series A", year: 2009, amount: 7000000, valuation: 35000000 },
      { round: "Series B", year: 2010, amount: 15000000, valuation: 100000000 },
      { round: "Series C", year: 2014, amount: 100000000, valuation: 1000000000 },
      { round: "Series D", year: 2016, amount: 450000000, valuation: 10000000000 },
    ],
    ipoValuation: 170000000000,
    ipoYear: 2015,
  },
  uber: {
    name: "Uber",
    fundingRounds: [
      { round: "Seed", year: 2010, amount: 1500000, valuation: 6000000 },
      { round: "Series A", year: 2011, amount: 11000000, valuation: 60000000 },
      { round: "Series B", year: 2013, amount: 258000000, valuation: 3700000000 },
      { round: "Series C", year: 2014, amount: 1200000000, valuation: 41000000000 },
      { round: "Series D", year: 2015, amount: 2000000000, valuation: 62500000000 },
    ],
    ipoValuation: 82000000000,
    ipoYear: 2019,
  },
};

interface FundingRound {
  round: string;
  year: number;
  amount: number;
  valuation: number;
}

export default function StartupComparisonTool() {
  const [selectedStartup, setSelectedStartup] = useState("stripe");
  const [userStartupName, setUserStartupName] = useState("My Startup");
  const [fundingRounds, setFundingRounds] = useState<FundingRound[]>([
    { round: "Seed", year: 2023, amount: 2000000, valuation: 20000000 },
  ]);
  const [projectedGrowthRate, setProjectedGrowthRate] = useState(50); // % annual growth
  const [ipoYear, setIpoYear] = useState(2030);

  // Load data from localStorage or URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let hasUrlParams = false;

    // First, try to load from URL parameters
    const startupName = urlParams.get('startupName');
    const growthRate = urlParams.get('growthRate');
    const ipoYearParam = urlParams.get('ipoYear');
    const fundingRoundsParam = urlParams.get('fundingRounds');

    if (startupName || growthRate || ipoYearParam || fundingRoundsParam) {
      hasUrlParams = true;
      if (startupName) setUserStartupName(startupName);
      if (growthRate) setProjectedGrowthRate(parseFloat(growthRate));
      if (ipoYearParam) setIpoYear(parseInt(ipoYearParam));

      if (fundingRoundsParam) {
        try {
          const rounds = JSON.parse(fundingRoundsParam);
          setFundingRounds(rounds);
        } catch (e) {
          console.error('Invalid funding rounds in URL');
        }
      }
    }

    // If no URL parameters, try to load from localStorage
    if (!hasUrlParams && typeof window !== 'undefined') {
      const savedConfig = localStorage.getItem('startup-comparison-config');
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          setUserStartupName(config.startupName || 'My Startup');
          setProjectedGrowthRate(config.growthRate || 50);
          setIpoYear(config.ipoYear || 2030);
          setFundingRounds(config.fundingRounds || [{ round: "Seed", year: 2023, amount: 2000000, valuation: 20000000 }]);
        } catch (e) {
          console.error('Error parsing saved startup config:', e);
        }
      }
    }
  }, []);

  // Save to localStorage whenever form data changes
  useEffect(() => {
    saveToLocalStorage();
  }, [userStartupName, projectedGrowthRate, ipoYear, fundingRounds]);

  const updateURL = () => {
    const urlParams = new URLSearchParams();

    if (userStartupName !== 'My Startup') urlParams.set('startupName', userStartupName);
    if (projectedGrowthRate !== 50) urlParams.set('growthRate', projectedGrowthRate.toString());
    if (ipoYear !== 2030) urlParams.set('ipoYear', ipoYear.toString());

    // Add funding rounds
    const defaultRounds = [{ round: "Seed", year: 2023, amount: 2000000, valuation: 20000000 }];
    if (JSON.stringify(fundingRounds) !== JSON.stringify(defaultRounds)) {
      urlParams.set('fundingRounds', JSON.stringify(fundingRounds));
    }

    const newURL = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  const saveToLocalStorage = () => {
    if (typeof window !== 'undefined') {
      const config = {
        startupName: userStartupName,
        growthRate: projectedGrowthRate,
        ipoYear,
        fundingRounds
      };
      localStorage.setItem('startup-comparison-config', JSON.stringify(config));
    }
  };

  const addFundingRound = () => {
    const lastRound = fundingRounds[fundingRounds.length - 1];
    const nextRound = lastRound.round === "Seed" ? "Series A" :
                     lastRound.round === "Series A" ? "Series B" :
                     lastRound.round === "Series B" ? "Series C" : "Series D";
    setFundingRounds([...fundingRounds, {
      round: nextRound,
      year: lastRound.year + 2,
      amount: lastRound.amount * 3,
      valuation: lastRound.valuation * 4,
    }]);
  };

  const removeFundingRound = (index: number) => {
    if (fundingRounds.length > 1) {
      setFundingRounds(fundingRounds.filter((_, i) => i !== index));
    }
  };

  const updateFundingRound = (index: number, field: keyof FundingRound, value: string | number) => {
    const updated = [...fundingRounds];
    updated[index] = { ...updated[index], [field]: value };
    setFundingRounds(updated);
  };

  const comparisonData = useMemo(() => {
    const startup = popularStartups[selectedStartup as keyof typeof popularStartups];
    const userData = fundingRounds.map(round => ({
      year: round.year,
      userValuation: round.valuation,
      userAmount: round.amount,
      round: round.round,
    }));

    const startupData = startup.fundingRounds.map(round => ({
      year: round.year,
      startupValuation: round.valuation,
      startupAmount: round.amount,
      round: round.round,
    }));

    // Merge data by year
    const allYears = [...new Set([...userData.map(d => d.year), ...startupData.map(d => d.year)])].sort();

    return allYears.map(year => {
      const userPoint = userData.find(d => d.year === year);
      const startupPoint = startupData.find(d => d.year === year);
      return {
        year,
        userValuation: userPoint?.userValuation || null,
        startupValuation: startupPoint?.startupValuation || null,
        userAmount: userPoint?.userAmount || null,
        startupAmount: startupPoint?.startupAmount || null,
        round: userPoint?.round || startupPoint?.round || '',
      };
    });
  }, [selectedStartup, fundingRounds]);

  const projectionData = useMemo(() => {
    const data = [];
    const lastRound = fundingRounds[fundingRounds.length - 1];
    let currentValuation = lastRound.valuation;
    let currentYear = lastRound.year;

    for (let year = currentYear; year <= ipoYear; year++) {
      data.push({
        year,
        projectedValuation: currentValuation,
        round: year === currentYear ? lastRound.round : 'Projection',
      });
      currentValuation *= (1 + projectedGrowthRate / 100);
    }

    // Add IPO
    data.push({
      year: ipoYear,
      projectedValuation: currentValuation,
      round: 'IPO',
    });

    return data;
  }, [fundingRounds, projectedGrowthRate, ipoYear]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };



  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-8">
        <div className="text-center mb-8">
           <h1 className="text-xl font-semibold text-foreground mb-2">
             Startup Valuation Comparison Tool
           </h1>
           <p className="text-sm text-muted-foreground">
             Compare your startup's funding journey with successful companies
           </p>

          <div className="flex justify-center mt-4">
            <Button onClick={updateURL} variant="outline">
              <Link className="w-4 h-4 mr-2" />
              Update URL
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Startup</CardTitle>
              <CardDescription>
                Configure your startup's funding rounds
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="startupName">Startup Name</Label>
                <Input
                  id="startupName"
                  value={userStartupName}
                  onChange={(e) => setUserStartupName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Funding Rounds</Label>
                {fundingRounds.map((round, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label>Round</Label>
                      <Input
                        value={round.round}
                        onChange={(e) => updateFundingRound(index, 'round', e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Year</Label>
                      <Input
                        type="number"
                        value={round.year}
                        onChange={(e) => updateFundingRound(index, 'year', Number(e.target.value))}
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        value={round.amount}
                        onChange={(e) => updateFundingRound(index, 'amount', Number(e.target.value))}
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Valuation ($)</Label>
                      <Input
                        type="number"
                        value={round.valuation}
                        onChange={(e) => updateFundingRound(index, 'valuation', Number(e.target.value))}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeFundingRound(index)}
                      disabled={fundingRounds.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button onClick={addFundingRound} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Funding Round
                </Button>
              </div>

              <hr className="my-4" />

              <div className="space-y-2">
                <Label htmlFor="growthRate">Projected Annual Growth Rate (%)</Label>
                <Input
                  id="growthRate"
                  type="number"
                  value={projectedGrowthRate}
                  onChange={(e) => setProjectedGrowthRate(Number(e.target.value))}
                  min="0"
                  max="200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ipoYear">Projected IPO Year</Label>
                <Input
                  id="ipoYear"
                  type="number"
                  value={ipoYear}
                  onChange={(e) => setIpoYear(Number(e.target.value))}
                  min={new Date().getFullYear()}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compare With</CardTitle>
              <CardDescription>
                Select a popular startup to compare against
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="compareStartup">Startup</Label>
                <Select value={selectedStartup} onValueChange={setSelectedStartup}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="shopify">Shopify</SelectItem>
                    <SelectItem value="uber">Uber</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">{popularStartups[selectedStartup as keyof typeof popularStartups].name}</h4>
                <p className="text-sm text-muted-foreground">
                  IPO: {formatCurrency(popularStartups[selectedStartup as keyof typeof popularStartups].ipoValuation)} in {popularStartups[selectedStartup as keyof typeof popularStartups].ipoYear}
                </p>
                <div className="space-y-1">
                  {popularStartups[selectedStartup as keyof typeof popularStartups].fundingRounds.map((round, index) => (
                    <div key={index} className="text-xs">
                      {round.round} ({round.year}): {formatCurrency(round.amount)} @ {formatCurrency(round.valuation)}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Latest Valuation</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(fundingRounds[fundingRounds.length - 1].valuation)}
                  </p>
                </div>
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Projected IPO Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(projectionData[projectionData.length - 1].projectedValuation)}
                  </p>
                </div>
                <Target className="w-6 h-6 text-blue-500" />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Raised</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(fundingRounds.reduce((sum, round) => sum + round.amount, 0))}
                  </p>
                </div>
                <TrendingUp className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Valuation Comparison Over Time</CardTitle>
              <CardDescription>
                Your startup vs {popularStartups[selectedStartup as keyof typeof popularStartups].name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  userValuation: {
                    label: userStartupName,
                    color: "hsl(var(--chart-1))",
                  },
                  startupValuation: {
                    label: popularStartups[selectedStartup as keyof typeof popularStartups].name,
                    color: "hsl(var(--chart-2))",
                  },
                }}
                className="w-full h-[400px]"
              >
                <LineChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(value) => formatCurrency(value).replace('$', '')} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value: any) => formatCurrency(value)} />} />
                  <ChartLegend />
                  <Line
                    type="monotone"
                    dataKey="userValuation"
                    stroke="var(--color-userValuation)"
                    strokeWidth={3}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="startupValuation"
                    stroke="var(--color-startupValuation)"
                    strokeWidth={3}
                    connectNulls={false}
                  />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Startup Projections</CardTitle>
              <CardDescription>
                Valuation growth from latest funding to IPO
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  projectedValuation: {
                    label: "Projected Valuation",
                    color: "hsl(var(--chart-3))",
                  },
                }}
                className="w-full h-[400px]"
              >
                <AreaChart data={projectionData}>
                  <defs>
                    <linearGradient id="projectionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(value) => formatCurrency(value).replace('$', '')} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value: any) => formatCurrency(value)} />} />
                  <Area
                    type="monotone"
                    dataKey="projectedValuation"
                    stroke="var(--color-projectedValuation)"
                    fillOpacity={1}
                    fill="url(#projectionGradient)"
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Funding Rounds Comparison</CardTitle>
              <CardDescription>
                Investment amounts raised in each funding round
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  userAmount: {
                    label: userStartupName,
                    color: "hsl(var(--chart-1))",
                  },
                  startupAmount: {
                    label: popularStartups[selectedStartup as keyof typeof popularStartups].name,
                    color: "hsl(var(--chart-2))",
                  },
                }}
                className="w-full h-[400px]"
              >
                <BarChart data={comparisonData.filter(d => d.userAmount !== null || d.startupAmount !== null)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(value) => formatCurrency(value).replace('$', '')} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value: any) => formatCurrency(value)} />} />
                  <ChartLegend />
                  <Bar
                    dataKey="userAmount"
                    fill="var(--color-userAmount)"
                    name={userStartupName}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="startupAmount"
                    fill="var(--color-startupAmount)"
                    name={popularStartups[selectedStartup as keyof typeof popularStartups].name}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}